import type { DailyScheduleDto } from "@/modules/boxDashboard/gateway/boxDashboardApiDto"
import type { UserDto } from "@/modules/profile/userProfile/userDto"
import dayjs from "dayjs"

export type SlotStatus = "past" | "current" | "upcoming"

export class DailyScheduleSlotEntity {
  constructor(
    public readonly slotId: string,
    public readonly slotStart: string | null,
    public readonly slotEnd: string | null,
    public readonly programName: string | null,
    public readonly color: string | null,
    public readonly assignedStaffMembers: UserDto | null,
    public readonly numberOfTrials: number | null,
    public readonly numberOfDropIns: number | null,
    public readonly numberOfParticipants: number | null,
    public readonly numberOfActualParticipants: number | null,
    public readonly numberOfPendingParticipants: number | null,
  ) {}

  static fromDto(dto: DailyScheduleDto): DailyScheduleSlotEntity {
    return new DailyScheduleSlotEntity(
      dto.slotId,
      dto.slotStart,
      dto.slotEnd,
      dto.programName,
      dto.color,
      dto.assignedStaffMembers,
      dto.numberOfTrials,
      dto.numberOfDropIns,
      dto.numberOfParticipants,
      dto.numberOfActualParticipants,
      dto.numberOfPendingParticipants,
    )
  }

  getStatus(): SlotStatus {
    const now = dayjs()
    const start = dayjs(this.slotStart)
    const end = dayjs(this.slotEnd)

    if (now.isAfter(end)) {
      return "past"
    }
    if (now.isAfter(start) && now.isBefore(end)) {
      return "current"
    }
    return "upcoming"
  }

  getStartTime(): string {
    return dayjs(this.slotStart ?? "").format("H:mm")
  }

  getEndTime(): string {
    return dayjs(this.slotEnd ?? "").format("H:mm")
  }

  getTimeRange(): string {
    return `${this.getStartTime()} - ${this.getEndTime()}`
  }

  getFillPercentage(): number {
    if (
      this.numberOfParticipants === 0 ||
      this.numberOfActualParticipants === null ||
      this.numberOfParticipants === null
    )
      return 0
    return Math.round((this.numberOfActualParticipants / this.numberOfParticipants) * 100)
  }

  hasWaitlist(): boolean {
    if (this.numberOfPendingParticipants === null) return false
    return this.numberOfPendingParticipants > 0
  }

  hasTrials(): boolean {
    return this.numberOfTrials !== null && this.numberOfTrials > 0
  }

  hasDropIns(): boolean {
    return this.numberOfDropIns !== null && this.numberOfDropIns > 0
  }

  hasCoach(): boolean {
    return this.assignedStaffMembers !== null
  }

  getCoachFullName(): string | null {
    if (!this.assignedStaffMembers) return null
    return `${this.assignedStaffMembers.firstname} ${this.assignedStaffMembers.lastname}`
  }

  getParticipantsSummary(): string {
    return `${this.numberOfActualParticipants ?? 0}👤 +${this.numberOfPendingParticipants ?? 0}`
  }
}
